/* eslint-disable import/order */
/* eslint-disable camelcase */
import { useContext, useEffect } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useUnleashContext } from '@unleash/proxy-client-react';
import { handleWellzAuth } from '@wellz/utils';
import { Auth } from 'aws-amplify';
import { withAuthenticator } from 'aws-amplify-react';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import { getMessageError } from 'utils/helpers';

import { SpecialistContext } from 'context';
import { useOnInit } from 'hooks';
import { useLogger } from 'hooks/useLogger/useLogger';
import { getAwsExports } from 'services/amplify';
import { getPartner } from 'services/bff';
import { savePartnerPreferredLanguage } from 'services/nvPartners';
import {
  getSpecialistInfo,
  SpecialistInfo,
  SpecialistRole,
} from 'services/specialist';
import { getSpecialistMfaConfig } from 'services/specialist/specialist';

import { SignIn } from 'pages/Login/SignIn';

import { JWT } from './types';
import { Any } from 'types/others';

if (typeof jest !== 'undefined') {
  // it's here to avoid a cyclic importing problem in vitest
  // if you remove this line and you see tests start failing, you'll now why I'm here
  // good luck trying to remove me :)
  // eslint-disable-next-line no-unused-expressions
  getPartner;
}

const withAuth = (
  WrappedPage: React.FC<any>,
  permissions?: SpecialistRole[],
) => {
  const AuthedPage: React.FC = ({ children, ...props }) => {
    const logger = useLogger();
    const navigate = useNavigate();
    const updateContext = useUnleashContext();

    const { specialistInfo, setSpecialistInfo, setAuthenticated } =
      useContext(SpecialistContext);

    const { data: specialistData } = useQuery({
      queryKey: ['get-specialist-info', specialistInfo?.id],
      queryFn: getSpecialistInfo,
      onSuccess: (specialist: SpecialistInfo) => {
        setSpecialistInfo({ ...specialistInfo, ...specialist });
      },
    });

    const { data: partner } = useQuery({
      queryKey: ['get-partner', specialistInfo?.id],
      queryFn: getPartner,
    });

    const { mutate: updatePreferredLanguage } = useMutation({
      mutationKey: ['update-preferred-language', partner?.id],
      mutationFn: savePartnerPreferredLanguage,
    });

    const getSpecialistRoles = (userAws: Any): string[] => {
      const jwtDecoded = jwtDecode<JWT>(
        userAws?.signInUserSession?.accessToken?.jwtToken,
      );

      return jwtDecoded['cognito:groups'];
    };

    const setSpecialistContext = async (currentUser: Any) => {
      let updatedSpecialistInfo = {
        ...specialistInfo,
        ...currentUser,
      };

      datadogRum.setUser({
        id: updatedSpecialistInfo?.username,
      });

      setSpecialistInfo(currentUser);

      await updateContext({
        userId: updatedSpecialistInfo?.username,
        properties: {
          groups: updatedSpecialistInfo?.groups,
        },
      });

      try {
        const { phone_number } = await getSpecialistMfaConfig();

        updatedSpecialistInfo = {
          ...updatedSpecialistInfo,
          phoneNumber: phone_number,
        };

        if (!specialistInfo?.fullName) {
          updatedSpecialistInfo = {
            ...updatedSpecialistInfo,
            ...specialistData,
            groups: updatedSpecialistInfo.groups,
          };
        }

        setSpecialistInfo(updatedSpecialistInfo);
      } catch (e) {
        logger.error(
          getMessageError(
            e,
            'Unexpected error while retrieving care provider information, it could lead to some malfunction',
          ),
        );
      }
    };

    const checkSpecialistPermissions = (roles: string[]) => {
      if (
        !roles ||
        (roles &&
          !roles.includes('specialist') &&
          !roles.includes('therapy-companion'))
      ) {
        navigate('/signout');
        return;
      }

      const hasPermission = (): boolean =>
        roles.some(role => permissions?.includes(role as SpecialistRole));

      if (roles && permissions && !hasPermission()) {
        navigate('/not-found');
      }
    };

    useOnInit(async () => {
      const config = await getAwsExports();

      try {
        let currentUser;

        try {
          const userAws = await Auth.currentAuthenticatedUser();
          const roles = getSpecialistRoles(userAws);
          currentUser = { ...userAws, groups: roles };
        } catch (e) {
          currentUser = null;
        }

        handleWellzAuth(
          {
            clientId: config.aws_user_pools_web_client_id,
          },
          currentUser,
        );

        if (!currentUser) return;

        setAuthenticated(true);
        checkSpecialistPermissions(currentUser.groups);
        setSpecialistContext(currentUser);
      } catch (error) {
        datadogLogs.logger.error('Failed to get current user info.', {
          error,
        });

        await Auth.signOut();
        handleWellzAuth({
          clientId: config.aws_user_pools_web_client_id,
        });
      }
    });

    useEffect(() => {
      if (navigator.language === partner?.preferredLanguage) return;

      updatePreferredLanguage();
    }, [partner, updatePreferredLanguage]);

    return <WrappedPage {...props}>{children}</WrappedPage>;
  };

  const AuthedPageWithAuthenticator = withAuthenticator(AuthedPage, false, [
    <SignIn key="SignIn" />,
  ]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderAuthedPage = ({ props }: any) => {
    const isLocal = process.env.NODE_ENV === 'development';

    if (isLocal) {
      return <AuthedPageWithAuthenticator {...props} />;
    }

    return <AuthedPage />;
  };

  // eslint-disable-next-line react/display-name, @typescript-eslint/no-explicit-any
  return (props: any) => {
    return renderAuthedPage(props);
  };
};

export default withAuth;
